import { useCallback } from 'react'
import { IPlano, IPlanoCompletoMultiRange, IPlanoMultiRangeRequest, IPlanoRange, TipoPlano } from '../../application/models'
import { parseValorApi } from '../../utils/formatters/plano/planoFormatter'
import { usePlanosVelesApi } from '../../infra/planos-veles'
import { useUIService } from '../ui'
import { useAppDispatch, useAppSelector } from '../../presentation/hooks/store-hooks'
import { setCreateClienteFormPlanoState, setEditClienteFormPlanoState } from '../../application/states/clientes'
import {
  setLoading,
  setError,
  setTipoPlano,
  setQuantidadeMaximaFee,
  setValorFee,
  setCamposComErro,
  setQuantidadePre,
  setExpiracaoPre,
  setValorPre,
  setValorUnitarioPre,
  setQuantidadePOC,
  setExpiracaoPOC,
  setDadosIniciaisCarregados,
  setLoadingObter,
  setRangesPosPago,
  setRangesFeeExcedente,
  setPlanoEmEdicao,
  setUltimoPlanoValido,
  resetFormularioPlanoState,
  resetPlanoState,
  setPlano,
  setPlanoCarregadoInicial,
  clearAll,
  criarPlanoRequestVazio
} from '../../application/states/planoVeles/planoVeles-slice'

export const usePlanoVelesService = () => {
  // Services
  const {
    inserirPlanoMultiRangeApi,
    atualizarPlanoMultiRangeApi,
    obterPlanoPorClienteApi
  } = usePlanosVelesApi()
  const { setAlert } = useUIService()
  const dispatch = useAppDispatch()

  const MAX_VALUE = 999999999;
  // ============= ESTADOS DO REDUX =============
  const {
    loading,
    error,
    loadingObter,
    dadosIniciaisCarregados,
    plano,
    tipoPlano,
    quantidadeMinimaFee,
    quantidadeMaximaFee,
    valorFee,
    quantidadePre,
    valorPre,
    valorUnitarioPre,
    expiracaoPre,
    quantidadePOC,
    expiracaoPOC,
    rangesPosPago,
    rangesFeeExcedente,
    camposComErro,
    planoEmEdicao,
    ultimoPlanoValido,
    planoCarregadoInicial,

  } = useAppSelector((state) => state.planoVeles)

  // Acessar estado do cliente para verificar se está ativo
  const editClienteForm = useAppSelector((state) => state.clientes.editClienteForm)

  // Valida e formata uma data para o estado
  const validarEFormatarData = useCallback((dataString?: string | Date): Date | null => {
    if (!dataString) return null

    try {
      const data = new Date(dataString)
      return isNaN(data.getTime()) ? null : data
    } catch {
      return null
    }
  }, [])

  // Inicializa ranges com valores padrão
  const inicializarRangesPadrao = useCallback((
    tipoPlano: IPlanoMultiRangeRequest['TipoPlano'],
    quantidadeMaximaFee: number = 0
  ) => {
    if (tipoPlano === TipoPlano.FeeMensal) {
      dispatch(setRangesFeeExcedente([{
        min: Math.min(Math.max(quantidadeMaximaFee + 1, 1), MAX_VALUE), max: MAX_VALUE, valor: 0
      }]))
      dispatch(setRangesPosPago([{ min: 0, max: MAX_VALUE, valor: 0.75 }]))
    } else if (tipoPlano === TipoPlano.PosPago) {
      dispatch(setRangesPosPago([{ min: 0, max: MAX_VALUE, valor: 0.75 }]))
    }
  }, [dispatch])

  // Inicializa formulário com valores padrão
  const inicializarFormularioPadrao = useCallback(() => {
    dispatch(setTipoPlano(TipoPlano.FeeMensal))
    dispatch(setQuantidadeMaximaFee(0))
    dispatch(setValorFee(0))
    dispatch(setQuantidadePre(0))
    dispatch(setValorPre(0))
    dispatch(setValorUnitarioPre(0))
    dispatch(setQuantidadePOC(0))

    const dataAtual = new Date()
    dispatch(setExpiracaoPre(dataAtual))
    dispatch(setExpiracaoPOC(dataAtual))

    inicializarRangesPadrao(TipoPlano.FeeMensal, 0)
    dispatch(setDadosIniciaisCarregados(true))
  }, [dispatch, inicializarRangesPadrao])

  // ==================== INICIALIZAÇÃO DE DADOS ====================

  // Função auxiliar para inicializar dados do formulário
  const inicializarDadosFormulario = useCallback((planoRedux: any) => {
    dispatch(setTipoPlano(planoRedux.TipoPlano))

    switch (planoRedux.TipoPlano) {
      case TipoPlano.FeeMensal:
        dispatch(setQuantidadeMaximaFee(planoRedux.QuantidadeMaximaFee || 0))
        dispatch(setValorFee(parseValorApi(planoRedux.ValorFee)))

        const rangesFee = Array.isArray(planoRedux.RangesFeeExcedente) && planoRedux.RangesFeeExcedente.length > 0
          ? planoRedux.RangesFeeExcedente.map((range: IPlanoRange) => ({
            ...range,
            valor: range.valor ?? 0
          }))
          : [{ min: Math.min((planoRedux.QuantidadeMaximaFee || 0) + 1, MAX_VALUE), max: MAX_VALUE, valor: 0 }]

        dispatch(setRangesFeeExcedente(rangesFee))
        break

      case TipoPlano.PosPago:
        const rangesPos = Array.isArray(planoRedux.RangesPosPago) && planoRedux.RangesPosPago.length > 0
          ? planoRedux.RangesPosPago.map((range: IPlanoRange) => ({
            ...range,
            valor: range.valor ?? 0
          }))
          : [{ min: 0, max: MAX_VALUE, valor: 0.75 }]

        dispatch(setRangesPosPago(rangesPos))
        break

      case TipoPlano.PrePago:
        dispatch(setQuantidadePre(planoRedux.QuantidadeMaximaPre || 0))
        dispatch(setValorPre(parseValorApi(planoRedux.ValorPre)))
        dispatch(setValorUnitarioPre(parseValorApi(planoRedux.ValorUnitarioPre)))
        dispatch(setExpiracaoPre(validarEFormatarData(planoRedux.ExpiracaoPre) ?? new Date()))
        break

      case TipoPlano.POC:
        dispatch(setQuantidadePOC(planoRedux.QuantidadePOC || 0))
        dispatch(setExpiracaoPOC(validarEFormatarData(planoRedux.ExpiracaoPOC) ?? new Date()))
        break
    }

    dispatch(setDadosIniciaisCarregados(true))
  }, [dispatch, validarEFormatarData])

  // Função auxiliar para inicializar dados reais do plano
  const inicializarDadosPlanoReal = useCallback((planoReal: IPlanoCompletoMultiRange) => {
    dispatch(setTipoPlano(planoReal.TipoPlano))

    if (planoReal.TipoPlano === TipoPlano.FeeMensal) {
      dispatch(setQuantidadeMaximaFee(planoReal.FeeQuantidadeMaxima || 0))
      dispatch(setValorFee(parseValorApi(planoReal.FeeValorMensal ?? 0)))

      const rangesFee = planoReal.RangesFeeExcedente && planoReal.RangesFeeExcedente.length > 0
        ? planoReal.RangesFeeExcedente.map((range: IPlanoRange) => ({
          ...range,
          valor: range.valor ?? 0
        }))
        : [{ min: 0, max: MAX_VALUE, valor: 0 }]

      dispatch(setRangesFeeExcedente(rangesFee))
    }
    else if (planoReal.TipoPlano === TipoPlano.PosPago) {
      const rangesPos = planoReal.RangesPosPago && planoReal.RangesPosPago.length > 0
        ? planoReal.RangesPosPago.map((range: IPlanoRange) => ({
          ...range,
          valor: range.valor ?? 0
        }))
        : [{ min: 0, max: MAX_VALUE, valor: 0.75 }]

      dispatch(setRangesPosPago(rangesPos))
    }
    else if (planoReal.TipoPlano === TipoPlano.PrePago) {
      dispatch(setQuantidadePre(planoReal.PreQuantidade || 0))
      dispatch(setValorPre(parseValorApi(planoReal.PreValorTotal ?? 0)))
      dispatch(setValorUnitarioPre(parseValorApi(planoReal.PreValorUnitario ?? 0)))
      dispatch(setExpiracaoPre(validarEFormatarData(planoReal.PreExpiracao) ?? new Date()))
    }
    else if (planoReal.TipoPlano === TipoPlano.POC) {
      dispatch(setQuantidadePOC(planoReal.POCQuantidade || 0))
      dispatch(setExpiracaoPOC(validarEFormatarData(planoReal.POCExpiracao) ?? new Date()))
    }

    dispatch(setDadosIniciaisCarregados(true))
  }, [dispatch, validarEFormatarData])

  const clearAllPlanoData = useCallback(() => {
    dispatch(clearAll())
  }, [dispatch])

  const resetPlanoFormOnClienteChange = useCallback(() => {
    dispatch(resetFormularioPlanoState())
    dispatch(setDadosIniciaisCarregados(false))
  }, [dispatch])

  const initializePlanoData = useCallback((
    shouldLoadData: boolean,
    editClienteForm: any,
    planoData: IPlanoCompletoMultiRange,
    dadosIniciaisCarregadosState: boolean
  ) => {
    if (dadosIniciaisCarregadosState) return

    if (shouldLoadData) {
      if (editClienteForm?.fields?.plano) {
        dispatch(setPlanoCarregadoInicial(editClienteForm.fields.plano))
        inicializarDadosFormulario(editClienteForm.fields.plano)
      }
      else if (planoData && planoData.TipoPlano) {
        dispatch(setPlanoCarregadoInicial(planoData))
        inicializarDadosPlanoReal(planoData)
      }
    }
    else {
      dispatch(setPlanoCarregadoInicial(criarPlanoRequestVazio()))
      inicializarFormularioPadrao()
    }
  }, [dispatch, inicializarFormularioPadrao, inicializarDadosFormulario, inicializarDadosPlanoReal])

  const restaurarPlanoCarregado = useCallback((tipo: IPlanoMultiRangeRequest['TipoPlano']) => {
    if (planoCarregadoInicial.IdCliente === 0) return false
    const tipoCarregado = planoCarregadoInicial.TipoPlano
    const match = tipoCarregado === tipo
    if (!match) return false
    const formatoApi = 'IdPlano' in planoCarregadoInicial
    if (formatoApi) {
      inicializarDadosPlanoReal(planoCarregadoInicial as IPlanoCompletoMultiRange)
    } else {
      inicializarDadosFormulario(planoCarregadoInicial as IPlanoMultiRangeRequest)
    }
    return true
  }, [planoCarregadoInicial, inicializarDadosFormulario, inicializarDadosPlanoReal])



  // ==================== OPERAÇÕES DE PLANO ====================

  const savePlanoFromForm = useCallback((dadosFormulario: Partial<IPlanoMultiRangeRequest>) => {
    if (!dadosFormulario?.TipoPlano) return false

    const dadosCompletos = {
      ...dadosFormulario,
      ...(dadosFormulario.TipoPlano === TipoPlano.FeeMensal && { QuantidadeMinimaFee: 1 }),
      ...(dadosFormulario.TipoPlano === TipoPlano.PrePago && { QuantidadeMinimaPre: 0 })
    }

    const planoAtualizado = {
      ...planoEmEdicao,
      ...dadosCompletos
    } as IPlanoMultiRangeRequest

    dispatch(setPlanoEmEdicao(planoAtualizado))
    dispatch(setEditClienteFormPlanoState({ plano: planoAtualizado }))

    return true
  }, [dispatch, planoEmEdicao])

  const inserirPlanoMultiRange = useCallback(async (data?: IPlanoMultiRangeRequest): Promise<IPlano | null> => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(''))

      let dadosParaInserir = data || planoEmEdicao || ultimoPlanoValido

      if (!dadosParaInserir?.TipoPlano) {
        dispatch(setError('Preencha os dados do plano Veles antes de criar'))
        return null
      }

      const result = await inserirPlanoMultiRangeApi(dadosParaInserir)
      if (dadosParaInserir) {
        dispatch(setUltimoPlanoValido(dadosParaInserir))
      }

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao inserir plano multi-range'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [inserirPlanoMultiRangeApi, dispatch, planoEmEdicao, ultimoPlanoValido])

  const atualizarPlanoMultiRange = useCallback(async (data: IPlanoMultiRangeRequest): Promise<IPlano | null> => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(''))

      const result = await atualizarPlanoMultiRangeApi(data)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar plano multi-range'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [atualizarPlanoMultiRangeApi, dispatch])

  const obterPlanoPorCliente = useCallback(async (IdCliente: number, clienteAtivo: boolean = true) => {
    try {
      dispatch(setLoadingObter(true))
      dispatch(setError(''))

      const planoData = await obterPlanoPorClienteApi({ IdCliente })
      const dadosReais = planoData?.data?.plano?.[0]

      if (dadosReais) {
        dispatch(setPlano(dadosReais))
      }

      return planoData
    } catch (error: any) {
      const status = error?.response?.status
      const errorMsg = error?.response?.data?.message?.toLowerCase() || ''
      const isNotFound = status === 404 || status === 400 || errorMsg.includes('encontrado') || errorMsg.includes('not found')

      // CASO 1: Cliente ativo sem plano - DEVE ALARMAR ERRO
      if (isNotFound && clienteAtivo) {
        const errorMessage = 'Cliente ativo precisa ter um plano cadastrado'
        dispatch(setError(errorMessage))
        setAlert({
          message: errorMessage,
          type: 'error'
        })
        throw error
      }

      // CASO 2: Cliente inativo sem plano - Trata silenciosamente e zera o estado
      if (isNotFound && !clienteAtivo) {
        dispatch(setPlano(null as any))
        return null
      }

      // CASO 3: Outros erros
      const errorMessage = error?.response?.data?.message || 'Erro ao obter plano do cliente'
      dispatch(setError(errorMessage))
      setAlert({
        message: errorMessage,
        type: 'error'
      })
      throw error
    } finally {
      dispatch(setLoadingObter(false))
    }
  }, [obterPlanoPorClienteApi, setAlert, dispatch])

  // ==================== UTILIDADES ====================

  const setCreateClienteFormPlano = useCallback((plano: IPlanoMultiRangeRequest) => {
    if (plano?.TipoPlano) {
      dispatch(setPlanoEmEdicao(plano))
      dispatch(setUltimoPlanoValido(plano))
      dispatch(setCreateClienteFormPlanoState({ plano }))
    }
  }, [dispatch])

  const validarRanges = useCallback((ranges: IPlanoRange[]): { isValid: boolean; errorMessage?: string } => {
    if (!ranges || ranges.length === 0) {
      return { isValid: true }
    }

    for (let i = 0; i < ranges.length - 1; i++) {
      if (ranges[i].max === MAX_VALUE) {
        return {
          isValid: false,
          errorMessage: 'Apenas o último range pode ter valor de 999.999.999'
        }
      }
    }

    return { isValid: true }
  }, [])

  const validatePlanoVeles = useCallback((
    planosVelesRef?: React.RefObject<any>,
    dadosPlano?: IPlanoMultiRangeRequest
  ): { isValid: boolean; data: IPlanoMultiRangeRequest | null; errorMessage?: string } => {
    // Se o cliente estiver inativo, não precisa validar o plano
    const clienteAtivo = editClienteForm?.fields?.Ativo ?? true; // Default true para criação de novos clientes
    if (!clienteAtivo) {
      return { isValid: true, data: null, errorMessage: '' };
    }

    // Verifica através de ref
    if (planosVelesRef?.current) {
      if (tipoPlano === TipoPlano.FeeMensal) {
        if (quantidadeMaximaFee === MAX_VALUE) {
          return {
            isValid: false,
            data: null,
            errorMessage: 'Apenas o último range pode ter valor de 999.999.999'
          }
        }

        if (rangesFeeExcedente && rangesFeeExcedente.length > 0) {
          const validacaoRanges = validarRanges(rangesFeeExcedente)
          if (!validacaoRanges.isValid) {
            return {
              isValid: false,
              data: null,
              errorMessage: validacaoRanges.errorMessage
            }
          }
        }
      }

      if (tipoPlano === TipoPlano.PosPago && rangesPosPago && rangesPosPago.length > 0) {
        const validacaoRanges = validarRanges(rangesPosPago)
        if (!validacaoRanges.isValid) {
          return {
            isValid: false,
            data: null,
            errorMessage: validacaoRanges.errorMessage
          }
        }
      }

      // Isso popula o camposComErro e retorna null se houver erros
      const planoData = planosVelesRef.current.getPlanoData?.()

      if (planoData) {
        if (planoData.TipoPlano === TipoPlano.FeeMensal) {
          if (planoData.QuantidadeMaximaFee === MAX_VALUE) {
            return {
              isValid: false,
              data: null,
              errorMessage: 'Apenas o último range pode ter valor de 999.999.999'
            }
          }

          if (planoData.RangesFeeExcedente && planoData.RangesFeeExcedente.length > 0) {
            const validacaoRanges = validarRanges(planoData.RangesFeeExcedente)
            if (!validacaoRanges.isValid) {
              return {
                isValid: false,
                data: null,
                errorMessage: validacaoRanges.errorMessage
              }
            }
          }
        }

        if (planoData.TipoPlano === TipoPlano.PosPago && planoData.RangesPosPago && planoData.RangesPosPago.length > 0) {
          const validacaoRanges = validarRanges(planoData.RangesPosPago)
          if (!validacaoRanges.isValid) {
            return {
              isValid: false,
              data: null,
              errorMessage: validacaoRanges.errorMessage
            }
          }
        }

        return { isValid: true, data: planoData, errorMessage: '' }
      } else {
        if (camposComErro.has('rangeInvalido')) {
          return {
            isValid: false,
            data: null,
            errorMessage: 'Apenas o último range pode ter valor de 999.999.999'
          }
        }

        const errorMessage = planosVelesRef.current.getErrorMessage?.() || "Preencha todos os campos obrigatórios do Plano Veles"
        return { isValid: false, data: null, errorMessage }
      }
    }

    // Verifica dados fornecidos diretamente
    const planoValido = dadosPlano || planoEmEdicao || ultimoPlanoValido
    if (planoValido?.TipoPlano) {
      if (planoValido.TipoPlano === TipoPlano.FeeMensal) {
        if (planoValido.QuantidadeMaximaFee === MAX_VALUE) {
          return {
            isValid: false,
            data: null,
            errorMessage: 'Apenas o último range pode ter valor de 999.999.999'
          }
        }

        if (planoValido.RangesFeeExcedente && planoValido.RangesFeeExcedente.length > 0) {
          const validacaoRanges = validarRanges(planoValido.RangesFeeExcedente)
          if (!validacaoRanges.isValid) {
            return {
              isValid: false,
              data: null,
              errorMessage: validacaoRanges.errorMessage
            }
          }
        }
      }

      if (planoValido.TipoPlano === TipoPlano.PosPago && planoValido.RangesPosPago && planoValido.RangesPosPago.length > 0) {
        const validacaoRanges = validarRanges(planoValido.RangesPosPago)
        if (!validacaoRanges.isValid) {
          return {
            isValid: false,
            data: null,
            errorMessage: validacaoRanges.errorMessage
          }
        }
      }

      return { isValid: true, data: planoValido, errorMessage: '' }
    }

    return {
      isValid: false,
      data: null,
      errorMessage: "Selecione e preencha os dados do plano Veles"
    }
  }, [planoEmEdicao, ultimoPlanoValido, tipoPlano, quantidadeMaximaFee, rangesFeeExcedente, rangesPosPago, camposComErro, validarRanges, editClienteForm?.fields?.Ativo])

  const savePlanoVelesTabData = useCallback((planosVelesRef: React.RefObject<any>): boolean => {
    let dadosParaSalvar: IPlanoMultiRangeRequest | null = null

    // Tenta obter dados do componente
    if (planosVelesRef.current?.getPlanoData) {
      try {
        const dadosComponente = planosVelesRef.current.getPlanoData()
        if (dadosComponente?.TipoPlano) {
          dadosParaSalvar = dadosComponente
        }
      } catch (error) {
        throw error
      }
    }

    // Fallback para dados do estado
    dadosParaSalvar = dadosParaSalvar || planoEmEdicao || ultimoPlanoValido

    if (dadosParaSalvar?.TipoPlano) {
      setCreateClienteFormPlano(dadosParaSalvar)
      return true
    }

    return false
  }, [setCreateClienteFormPlano, planoEmEdicao, ultimoPlanoValido])

  const clearError = useCallback(() => {
    dispatch(setError(''))
  }, [dispatch])

  const resetFormPlano = useCallback(() => {
    dispatch(resetFormularioPlanoState())
  }, [dispatch])

  const resetPlano = useCallback(() => {
    dispatch(resetPlanoState())
  }, [dispatch])

  // ==================== NORMALIZAÇÃO E VALIDAÇÃO ====================

  const savePlanoDataToService = useCallback((IdCliente: number) => {
    if (!tipoPlano) return

    const formData: Partial<IPlanoMultiRangeRequest> = {
      TipoPlano: tipoPlano,
      IdCliente: IdCliente
    }

    switch (tipoPlano) {
      case TipoPlano.FeeMensal:
        formData.QuantidadeMinimaFee = 1
        formData.QuantidadeMaximaFee = quantidadeMaximaFee
        formData.ValorFee = valorFee

        if (rangesFeeExcedente.length > 0) {
          formData.RangesFeeExcedente = rangesFeeExcedente
          const primeiroRange = rangesFeeExcedente[0]
          formData.QuantidadeMinimaExcedente = primeiroRange.min
          formData.ValorUnitarioExcedente = primeiroRange.valor ?? undefined
        }
        break

      case TipoPlano.PosPago:
        if (rangesPosPago.length > 0) {
          formData.RangesPosPago = rangesPosPago
        }
        break

      case TipoPlano.PrePago:
        formData.QuantidadeMinimaPre = 0
        formData.QuantidadeMaximaPre = quantidadePre
        formData.ValorPre = valorPre
        formData.ValorUnitarioPre = valorUnitarioPre
        if (expiracaoPre) {
          formData.ExpiracaoPre = expiracaoPre
        }
        break

      case TipoPlano.POC:
        formData.QuantidadePOC = quantidadePOC
        if (expiracaoPOC) {
          formData.ExpiracaoPOC = expiracaoPOC
        }
        break
    }

    if (formData.TipoPlano) {
      const planoCompleto = formData as IPlanoMultiRangeRequest
      setCreateClienteFormPlano(planoCompleto)
    }

    savePlanoFromForm(formData)
  }, [
    tipoPlano, quantidadeMaximaFee, valorFee,
    rangesFeeExcedente, rangesPosPago, quantidadePre, valorPre, valorUnitarioPre,
    expiracaoPre, quantidadePOC, expiracaoPOC, savePlanoFromForm, setCreateClienteFormPlano
  ])

  const validarFormatarDadosPlano = useCallback((IdCliente: number): IPlanoMultiRangeRequest | null => {
    if (!tipoPlano) return null

    const novosErros = new Set<string>()
    const planoData: IPlanoMultiRangeRequest = {
      IdCliente: IdCliente,
      TipoPlano: tipoPlano
    }

    const validacoesPorTipo: Record<number, () => void> = {
      [TipoPlano.FeeMensal]: () => {
        if (quantidadeMaximaFee === undefined || quantidadeMaximaFee < 1) {
          novosErros.add('quantidadeMaximaFee')
        }

        if (quantidadeMaximaFee === MAX_VALUE) {
          novosErros.add('rangeInvalido')
        }

        if (valorFee === undefined || valorFee <= 0) {
          novosErros.add('valorFee')
        }
        if (rangesFeeExcedente.length === 0) {
          novosErros.add('rangesFeeExcedente')
        }

        if (rangesFeeExcedente.length > 0) {
          for (let i = 0; i < rangesFeeExcedente.length - 1; i++) {
            if (rangesFeeExcedente[i].max === MAX_VALUE) {
              novosErros.add('rangeInvalido')
              break
            }
          }
        }

        rangesFeeExcedente.forEach((range, index) => {
          const v = range.valor
          if (typeof v === 'number' && Number.isFinite(v) && (Math.round(v * 10000) / 10000) !== v) {
            novosErros.add(`rangeExcedenteDecimais${index}`)
          }
        })

        planoData.QuantidadeMinimaFee = 1
        planoData.QuantidadeMaximaFee = quantidadeMaximaFee
        planoData.ValorFee = valorFee || 0

        if (rangesFeeExcedente.length > 0 && quantidadeMaximaFee !== undefined) {
          const minAjustado = Math.min(quantidadeMaximaFee + 1, MAX_VALUE)
          const primeiroRange = rangesFeeExcedente[0]

          planoData.QuantidadeMinimaExcedente = minAjustado
          planoData.ValorUnitarioExcedente = primeiroRange.valor ?? 0
          planoData.RangesFeeExcedente = rangesFeeExcedente.map((range, index) =>
            index === 0 ? { ...range, min: minAjustado } : range
          )
        }
      },

      [TipoPlano.PosPago]: () => {
        if (rangesPosPago.length === 0) {
          novosErros.add('rangesPosPago')
        }

        rangesPosPago.forEach((range, index) => {
          if (range.valor === undefined || range.valor === null || range.valor <= 0) {
            novosErros.add(`rangePosPago${index}`)
          }
          const v = range.valor
          if (typeof v === 'number' && Number.isFinite(v) && (Math.round(v * 10000) / 10000) !== v) {
            novosErros.add(`rangePosPagoDecimais${index}`)
          }
        })

        if (rangesPosPago.length > 0) {
          for (let i = 0; i < rangesPosPago.length - 1; i++) {
            if (rangesPosPago[i].max === MAX_VALUE) {
              novosErros.add('rangeInvalido')
              break
            }
          }
        }

        if (rangesPosPago.length > 0) {
          planoData.RangesPosPago = rangesPosPago
        }
      },

      [TipoPlano.PrePago]: () => {
        if (quantidadePre === undefined || quantidadePre <= 0) {
          novosErros.add('quantidadePre')
        }
        if (valorPre === undefined || valorPre <= 0) {
          novosErros.add('valorPre')
        }
        if (!expiracaoPre || !(expiracaoPre instanceof Date) || isNaN(expiracaoPre.getTime())) {
          novosErros.add('expiracaoPre')
        }

        planoData.QuantidadeMinimaPre = 0
        planoData.QuantidadeMaximaPre = quantidadePre
        planoData.ValorPre = valorPre
        planoData.ValorUnitarioPre = valorUnitarioPre
        if (expiracaoPre) {
          planoData.ExpiracaoPre = expiracaoPre
        }
      },

      [TipoPlano.POC]: () => {
        if (quantidadePOC === undefined || quantidadePOC <= 0) {
          novosErros.add('quantidadePOC')
        }
        if (!expiracaoPOC || !(expiracaoPOC instanceof Date) || isNaN(expiracaoPOC.getTime())) {
          novosErros.add('expiracaoPOC')
        }

        planoData.QuantidadePOC = quantidadePOC
        if (expiracaoPOC) {
          planoData.ExpiracaoPOC = expiracaoPOC
        }
      }
    }

    // Executa validações do tipo específico
    if (validacoesPorTipo[tipoPlano]) {
      validacoesPorTipo[tipoPlano]()
    }

    // Validações de ranges
    const validarRanges = (ranges: IPlanoRange[], prefix: string) => {
      ranges.forEach((range, i) => {
        if (range.valor === undefined || range.valor === null || range.valor <= 0) {
          novosErros.add(`${prefix}${i}`)
        }

        if (i !== ranges.length - 1) {
          if (range.max === undefined || range.max === null || range.max <= range.min) {
            novosErros.add(`${prefix}Max${i}`)
          }
        }
      })
    }

    if (tipoPlano === TipoPlano.FeeMensal) {
      validarRanges(rangesFeeExcedente, 'rangeExcedente')
    } else if (tipoPlano === TipoPlano.PosPago) {
      validarRanges(rangesPosPago, 'rangePosPago')
    }

    dispatch(setCamposComErro(novosErros))
    return novosErros.size > 0 ? null : planoData
  }, [
    tipoPlano, quantidadeMaximaFee, valorFee, rangesFeeExcedente,
    rangesPosPago, quantidadePre, valorPre, valorUnitarioPre,
    expiracaoPre, quantidadePOC, expiracaoPOC, dispatch
  ])

  const normalizarRanges = useCallback((ranges?: IPlanoRange[]): string | undefined => {
    if (!ranges?.length) return undefined
    return ranges.map(range => `${range.min},${range.max},${range.valor}`).join('|')
  }, [])

  const normalizarPlano = useCallback((data: IPlanoMultiRangeRequest): IPlanoMultiRangeRequest => {
    const dadosNormalizados = { ...data }

    if (dadosNormalizados.RangesPosPago?.length) {
      (dadosNormalizados as any).RangesPosPago = normalizarRanges(dadosNormalizados.RangesPosPago)
    }

    if (dadosNormalizados.RangesFeeExcedente?.length) {
      (dadosNormalizados as any).RangesFeeExcedente = normalizarRanges(dadosNormalizados.RangesFeeExcedente)
    }

    return dadosNormalizados
  }, [normalizarRanges])

  // ==================== OPERAÇÕES COM RANGES ====================

  const recalculateRanges = useCallback((
    ranges: IPlanoRange[],
    tipo: 'posPago' | 'feeExcedente',
    quantidadeMaximaFee?: number
  ): IPlanoRange[] => {
    if (ranges.length === 0) return ranges;

    const newRanges = ranges.map(range => ({ ...range }));

    if (tipo === 'feeExcedente' && quantidadeMaximaFee !== undefined) {
      newRanges[0].min = Math.min(Math.max(quantidadeMaximaFee + 1, 1), MAX_VALUE);
    } else {
      newRanges[0].min = 1;
    }

    if (newRanges.length > 1) {
      const lastIndex = newRanges.length - 1;


      for (let i = 1; i < lastIndex; i++) {
        newRanges[i].min = Math.min(newRanges[i - 1].max + 1, MAX_VALUE);
        if (newRanges[i].max === 0 || newRanges[i].max === undefined || newRanges[i].max === null) {
          newRanges[i].max = Math.min(newRanges[i].min + 1, MAX_VALUE);
        }
      }

      newRanges[lastIndex].min = Math.min(newRanges[lastIndex - 1].max + 1, MAX_VALUE);

      if (newRanges[lastIndex].max === 0 || newRanges[lastIndex].max === undefined || newRanges[lastIndex].max === null) {
        newRanges[lastIndex].max = MAX_VALUE;
      }
    } else {
      if (newRanges[0].max === 0 || newRanges[0].max === undefined || newRanges[0].max === null) {
        newRanges[0].max = MAX_VALUE;
      }
    }

    const result = newRanges.map(range => ({
      min: range.min,
      max: range.max,
      valor: range.valor ?? 0
    }));
    return result;
  }, []);

  const recalculateRangesPosPago = useCallback((ranges: IPlanoRange[]): IPlanoRange[] => {
    return recalculateRanges(ranges, 'posPago')
  }, [recalculateRanges])

  const recalculateRangesFee = useCallback((ranges: IPlanoRange[], qtdMaximaFee: number): IPlanoRange[] => {
    return recalculateRanges(ranges, 'feeExcedente', qtdMaximaFee)
  }, [recalculateRanges])

  const gerenciarRanges = useCallback((
    tipo: 'posPago' | 'feeExcedente',
    operacao: 'add' | 'remove' | 'update',
    index?: number,
    field?: keyof IPlanoRange,
    value?: number | ''
  ) => {
    const stateRanges = tipo === 'posPago' ? rangesPosPago : rangesFeeExcedente;
    const setter = tipo === 'posPago' ? setRangesPosPago : setRangesFeeExcedente;
    const recalcFunc = tipo === 'posPago'
      ? recalculateRangesPosPago
      : (ranges: IPlanoRange[]) => recalculateRangesFee(ranges, quantidadeMaximaFee);

    let newRanges: IPlanoRange[];

    switch (operacao) {
      case 'add':
        newRanges = [...stateRanges, { min: 1, max: MAX_VALUE, valor: 0 }];
        break;

      case 'remove':
        if (stateRanges.length <= 1 || index === undefined) return;
        newRanges = stateRanges.filter((_, i) => i !== index);
        break;

      case 'update':
        if (index === undefined || field === undefined || value === undefined) return;
        newRanges = stateRanges.map((range, i) => {
          if (i === index) {
            return { ...range, [field]: value };
          }
          return range;
        });
        break;

      default:
        return;
    }

    const recalculatedRanges = recalcFunc(newRanges);
    dispatch(setter(recalculatedRanges));
  }, [rangesPosPago, rangesFeeExcedente, quantidadeMaximaFee, dispatch, recalculateRangesPosPago, recalculateRangesFee]);

  const addRangePosPago = useCallback(() => {
    gerenciarRanges('posPago', 'add')
  }, [gerenciarRanges])

  const removeRangePosPago = useCallback((index: number) => {
    gerenciarRanges('posPago', 'remove', index)
  }, [gerenciarRanges])

  const updateRangePosPago = useCallback((index: number, field: keyof IPlanoRange, value: number | '') => {
    gerenciarRanges('posPago', 'update', index, field, value)
  }, [gerenciarRanges])

  const addRangeFeeExcedente = useCallback(() => {
    gerenciarRanges('feeExcedente', 'add')
  }, [gerenciarRanges])

  const removeRangeFeeExcedente = useCallback((index: number) => {
    gerenciarRanges('feeExcedente', 'remove', index)
  }, [gerenciarRanges])

  const updateRangeFeeExcedente = useCallback((index: number, field: keyof IPlanoRange, value: number | '') => {
    gerenciarRanges('feeExcedente', 'update', index, field, value)
  }, [gerenciarRanges])

  const handlePlanoCalculation = useCallback(() => {

    if (!dadosIniciaisCarregados) {
      return;
    }

    if (tipoPlano === TipoPlano.PrePago) {
      if (quantidadePre && valorPre && quantidadePre > 0) {
        const valorCalculado = valorPre / quantidadePre;
        dispatch(setValorUnitarioPre(Number(valorCalculado.toFixed(4))));
      } else {
        dispatch(setValorUnitarioPre(0));
      }
    }

    if (tipoPlano === TipoPlano.FeeMensal && rangesFeeExcedente.length > 0) {
      const novoMin = Math.min(Math.max((quantidadeMaximaFee || 0) + 1, 1), MAX_VALUE);

      if (rangesFeeExcedente[0].min !== novoMin) {
        const novosRanges = rangesFeeExcedente.map(range => ({ ...range }));
        novosRanges[0].min = novoMin;

        const rangesRecalculados = recalculateRangesFee(novosRanges, quantidadeMaximaFee);
        dispatch(setRangesFeeExcedente(rangesRecalculados));
      }
    }
  }, [
    dadosIniciaisCarregados,
    tipoPlano,
    quantidadePre,
    valorPre,
    quantidadeMaximaFee,
    rangesFeeExcedente,
    recalculateRangesFee,
    dispatch
  ]);

  return {
    // Estados
    loading,
    error,
    plano,
    tipoPlano,
    quantidadeMinimaFee,
    quantidadeMaximaFee,
    valorFee,
    quantidadePre,
    valorPre,
    valorUnitarioPre,
    expiracaoPre,
    quantidadePOC,
    expiracaoPOC,
    rangesPosPago,
    rangesFeeExcedente,
    camposComErro,
    dadosIniciaisCarregados,
    loadingObter,
    planoEmEdicao,
    ultimoPlanoValido,

    // API Methods
    inserirPlanoMultiRange,
    atualizarPlanoMultiRange,
    obterPlanoPorCliente,

    // Utility Methods
    setCreateClienteFormPlano,
    savePlanoFromForm,
    savePlanoVelesTabData,
    validatePlanoVeles,
    clearError,
    resetFormPlano,
    resetPlano,
    normalizarPlano,
    validarFormatarDadosPlano,
    savePlanoDataToService,
    initializePlanoData,
    handlePlanoCalculation,
    clearAllPlanoData,
    resetPlanoFormOnClienteChange,
    restaurarPlanoCarregado,

    // Range Methods
    recalculateRangesPosPago,
    addRangePosPago,
    removeRangePosPago,
    updateRangePosPago,
    recalculateRangesFee,
    addRangeFeeExcedente,
    removeRangeFeeExcedente,
    updateRangeFeeExcedente,
  }
}